import {memo, MouseEvent, useCallback, useMemo} from 'react';
import {KindergartenData} from '@/map/@types';
import {AROUND_DATA_OPTIONS, MAX_VISIBLE_ZOOM_METER} from '@/map/constants';
import {AreaAnalyticsFilter} from '@/map/enums';
import {useVisibleZoomLevel} from '@/map/hooks';
import {convertToCoordFromPointCoords} from '@/map/utils';
import {SpotMarker} from '../../Marker';

const markerType = AreaAnalyticsFilter.KINDERGARTEN;
const typeInfo = AROUND_DATA_OPTIONS[markerType];

interface IProps {
  map?: naver.maps.Map;
  items?: KindergartenData[];
  selectedItem?: KindergartenData;
  onClick?(selected: {type: typeof markerType; index: number}): void;
}

interface MarkerProps {
  map: naver.maps.Map;
  id: string;
  item: KindergartenData;
  active?: boolean;
  onClick?(e: MouseEvent<HTMLDivElement>): void;
}

export const KindergartenMarkers = ({
  map,
  items,
  selectedItem,
  onClick,
}: IProps) => {
  const isVisibleZoom = useVisibleZoomLevel(
    MAX_VISIBLE_ZOOM_METER.mobile_markers
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const id = e.currentTarget.dataset.id;

      if (id) {
        onClick?.({type: markerType, index: Number(id)});
      }
    },
    [onClick]
  );

  if (!map || !items || !isVisibleZoom) {
    return null;
  }

  return (
    <>
      {items.map((i, idx) => (
        <Marker
          key={idx}
          map={map}
          id={String(idx)}
          item={i}
          active={selectedItem?.name === i.name}
          onClick={handleClick}
        />
      ))}
    </>
  );
};

const Marker = memo(({item, ...props}: MarkerProps) => {
  const position = useMemo(
    () => convertToCoordFromPointCoords(item?.location?.coordinates),
    [item?.location?.coordinates]
  );

  if (!item || !position) {
    return null;
  }

  return (
    <SpotMarker
      {...props}
      position={position}
      name={item.name}
      color={typeInfo.mobile?.color}
      typeLabel={typeInfo.label}
      icon={typeInfo.mobile?.icon}
    />
  );
});
